import React, { useState, useEffect } from 'react';
import { Mail, ShieldCheck, ArrowRight, Loader2, AlertCircle, MessageCircle, Smartphone } from 'lucide-react';
import logo from '../assets/logo.png';
import { config } from '../config';
import { agentSessionClient } from '../services/agentSession';
import { setAuthData } from '../services/api';

interface AuthViewProps {
    onAuthSuccess: (user: { email: string; role: string; [key: string]: any }) => void;
}

const AuthView = ({ onAuthSuccess }: AuthViewProps) => {
    const [step, setStep] = useState<'email' | 'otp'>('email');
    const [email, setEmail] = useState('');
    const [otp, setOtp] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [timer, setTimer] = useState(900); // 15 minutes in seconds
    const [deliveryMethod, setDeliveryMethod] = useState<'email' | 'whatsapp'>('email');
    const [phoneNumber, setPhoneNumber] = useState('');

    // Restore session on mount if already authenticated
    useEffect(() => {
        const existingEmail = localStorage.getItem(config.USER_EMAIL_KEY);
        if (existingEmail) {
            agentSessionClient.setUserId(existingEmail);
            agentSessionClient.restoreSession().then(session => {
                if (session) {
                    console.log('[AuthView] Restored existing session:', session.sessionId);
                }
            });
        }
    }, []);

    useEffect(() => {
        let interval: ReturnType<typeof setInterval> | null = null;
        if (step === 'otp' && timer > 0) {
            interval = setInterval(() => {
                setTimer((prev) => prev - 1);
            }, 1000);
        } else if (timer === 0) {
            setError('OTP expired. Please request a new one.');
        }
        return () => {
            if (interval) {clearInterval(interval);}
        };
    }, [step, timer]);

    const formatTime = (seconds: number): string => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const normalizePhone = (input: string): string | undefined => {
        const digits = input.replace(/[^0-9]/g, '');
        if (digits.length < 7) {return undefined;}
        return digits.startsWith('0') ? '27' + digits.slice(1) : digits;
    };

    const handleEmailSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        const normalizedEmail = email.toLowerCase().trim();
        const isAdmin = normalizedEmail === config.ADMIN_EMAIL.toLowerCase();

        try {
            const response = await fetch(config.AUTH_API_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'request_otp',
                    email: normalizedEmail,
                    isAdmin,
                    deliveryMethod,
                    phoneNumber: deliveryMethod === 'whatsapp' ? normalizePhone(phoneNumber) : undefined,
                })
            }).catch(err => {
                if (err.message === 'Failed to fetch') {
                    throw new Error('Connection Error: Please check your internet or server status.');
                }
                throw err;
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || errorData.error || 'OTP Request Failed. Please try again.');
            }

            const data = await response.json();
            if (!data.success) {
                throw new Error(data.error || 'OTP Request Failed. Please try again.');
            }

            setStep('otp');
            setTimer(900);
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'OTP Request Failed. Please try again.';
            setError(message);
        } finally {
            setLoading(false);
        }
    };

    const handleOtpSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const response = await fetch(config.AUTH_API_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'verify_otp', email, otp })
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || errorData.error || 'Invalid or expired OTP.');
            }

            const data = await response.json();
            if (!data.success) {
                throw new Error(data.error || 'Invalid or expired OTP.');
            }

            const normalizedRole = data.role ? data.role.toUpperCase() : 'CUSTOMER';
            localStorage.setItem(config.USER_EMAIL_KEY, email);
            localStorage.setItem(config.USER_ROLE_KEY, normalizedRole);
            if (data.sheetUrl || data.Client_Sheet_URL) {
                localStorage.setItem('SHEET_URL', data.sheetUrl || data.Client_Sheet_URL);
            }

            // Store auth data including token
            setAuthData(data);

            // Restore existing session or create a new one
            agentSessionClient.setUserId(email);
            const existing = await agentSessionClient.restoreSession();
            if (existing) {
                console.log('[AuthView] Restored session:', existing.sessionId);
            } else {
                const newSession = await agentSessionClient.saveSession({
                    email,
                    role: normalizedRole,
                    context: { memoryReferences: [] },
                    conversationHistory: [],
                });
                console.log('[AuthView] Created new session:', newSession?.sessionId);
            }

            onAuthSuccess({ email, role: normalizedRole, ...data });
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Invalid or expired OTP.';
            setError(message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-gradient-to-br from-slate-50 to-blue-50">
            <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl p-8 border border-blue-100/50 animate-in fade-in zoom-in duration-500">
                <div className="flex flex-col items-center mb-10">
                    <div className="bg-blue-50 p-4 rounded-2xl mb-4">
                        <img src={logo} alt="Logo" className="h-16 w-16 object-contain" />
                    </div>
                    <h1 className="text-3xl font-black text-slate-800 tracking-tight">BizBuilder Platform</h1>
                    <p className="text-slate-500 font-medium mt-1">App Command Center</p>
                </div>

                {step === 'email' ? (
                    <form onSubmit={handleEmailSubmit} className="space-y-6">
                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-2 ml-1">Email Address</label>
                            <div className="relative">
                                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                                <input
                                    type="email"
                                    required
                                    placeholder="name@company.com"
                                    className="w-full pl-12 pr-4 py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl focus:border-blue-500 focus:bg-white outline-none transition-all text-lg"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                />
                            </div>
                        </div>

                        {/* OTP Delivery Method Selection */}
                        <div className="space-y-3">
                            <label className="block text-sm font-bold text-slate-700 ml-1">Send OTP via</label>
                            <div className="grid grid-cols-2 gap-3">
                                <button
                                    type="button"
                                    onClick={() => setDeliveryMethod('email')}
                                    className={`flex items-center justify-center gap-2 py-3 px-4 rounded-2xl border-2 transition-all font-bold text-sm ${
                                        deliveryMethod === 'email'
                                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                                            : 'border-slate-100 bg-slate-50 text-slate-500 hover:border-slate-200'
                                    }`}
                                >
                                    <Mail size={18} />
                                    Email
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setDeliveryMethod('whatsapp')}
                                    className={`flex items-center justify-center gap-2 py-3 px-4 rounded-2xl border-2 transition-all font-bold text-sm ${
                                        deliveryMethod === 'whatsapp'
                                            ? 'border-green-500 bg-green-50 text-green-700'
                                            : 'border-slate-100 bg-slate-50 text-slate-500 hover:border-slate-200'
                                    }`}
                                >
                                    <MessageCircle size={18} />
                                    WhatsApp
                                </button>
                            </div>
                        </div>

                        {/* WhatsApp Phone Number Field */}
                        {deliveryMethod === 'whatsapp' && (
                            <div className="animate-in fade-in slide-in-from-top-2 duration-300">
                                <label className="block text-sm font-bold text-slate-700 mb-2 ml-1">WhatsApp Number</label>
                                <div className="relative">
                                    <Smartphone className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                                    <input
                                        type="tel"
                                        required
                                        placeholder="e.g. 082 345 6789"
                                        className="w-full pl-12 pr-4 py-4 bg-green-50 border-2 border-green-100 rounded-2xl focus:border-green-500 focus:bg-white outline-none transition-all text-lg"
                                        value={phoneNumber}
                                        onChange={(e) => setPhoneNumber(e.target.value)}
                                    />
                                </div>
                                <p className="text-xs text-slate-500 mt-2 ml-1">Enter your mobile number with country code or local format</p>
                            </div>
                        )}

                        {error && (
                            <div className="flex items-center gap-2 text-red-600 bg-red-50 p-4 rounded-xl text-sm font-medium">
                                <AlertCircle size={18} /> {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-blue-700 hover:bg-blue-800 text-white font-bold py-4 rounded-2xl shadow-lg shadow-blue-200 active:scale-[0.98] transition-all flex justify-center items-center gap-2 text-lg group"
                        >
                            {loading ? <Loader2 className="animate-spin" /> : <>Continue <ArrowRight className="group-hover:translate-x-1 transition-transform" /></>}
                        </button>
                    </form>
                ) : (
                    <form onSubmit={handleOtpSubmit} className="space-y-6">
                        <div className="text-center mb-6">
                            <p className="text-slate-600">
                                We've sent a code to <br />
                                {deliveryMethod === 'whatsapp' ? (
                                    <span className="font-bold text-green-700">WhatsApp ({phoneNumber})</span>
                                ) : (
                                    <span className="font-bold text-slate-900">{email}</span>
                                )}
                            </p>
                        </div>

                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-2 ml-1">Verification Code</label>
                            <div className="relative">
                                <ShieldCheck className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                                <input
                                    type="text"
                                    required
                                    maxLength={6}
                                    placeholder="000 000"
                                    className="w-full pl-12 pr-4 py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl focus:border-blue-500 focus:bg-white outline-none transition-all text-center text-2xl font-mono tracking-[0.5em]"
                                    value={otp}
                                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                                />
                            </div>
                            <div className="flex justify-between items-center mt-3 px-1">
                                <p className="text-xs font-medium text-slate-500">
                                    Expires in{' '}
                                    <span className={timer < 60 ? 'text-orange-500 font-bold' : ''}>
                                        {formatTime(timer)}
                                    </span>
                                </p>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setStep('email');
                                        setOtp('');
                                        setTimer(900);
                                        setError('');
                                    }}
                                    className="text-xs font-bold text-blue-600 hover:text-blue-700"
                                >
                                    Change Email
                                </button>
                            </div>
                        </div>

                        {error && (
                            <div className="flex items-center gap-2 text-red-600 bg-red-50 p-4 rounded-xl text-sm font-medium">
                                <AlertCircle size={18} /> {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={loading || timer === 0}
                            className="w-full bg-blue-700 hover:bg-blue-800 text-white font-bold py-4 rounded-2xl shadow-lg shadow-blue-200 active:scale-[0.98] transition-all flex justify-center items-center gap-2 text-lg disabled:opacity-50 disabled:shadow-none"
                        >
                            {loading ? <Loader2 className="animate-spin" /> : 'Verify & Login'}
                        </button>
                    </form>
                )}

                <div className="mt-8 text-center text-slate-400 text-xs font-medium">
                    Secure access powered by Chrysalinks
                </div>
            </div>
        </div>
    );
};

export default AuthView;
